/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `file_id` message.
 *
 * Must be first message in file.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class FileIdMesg : Mesg {
    public constructor() : super("FileId", Profile.MesgNum.FILE_ID)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** type. */
    public var type: File?
        get() = (getFieldValue(TYPE_FIELD_NUM) as? UByte)?.let { File.fromValue(it) }
        set(value) = setFieldValue(TYPE_FIELD_NUM, value?.value)

    /** manufacturer. */
    public var manufacturer: Manufacturer?
        get() = (getFieldValue(MANUFACTURER_FIELD_NUM) as? UShort)?.let { Manufacturer.fromValue(it) }
        set(value) = setFieldValue(MANUFACTURER_FIELD_NUM, value?.value)

    /** product. */
    public var product: UShort?
        get() = getFieldValue(PRODUCT_FIELD_NUM) as? UShort
        set(value) = setFieldValue(PRODUCT_FIELD_NUM, value)

    /**
     * favero_product: what product means when this message selects it.
     */
    public val faveroProduct: FaveroProduct?
        get() = (getFieldValue(PRODUCT_FIELD_NUM, 0, SubFieldSelector.Named("FaveroProduct")) as? UShort)?.let { FaveroProduct.fromValue(it) }

    public fun setFaveroProduct(value: FaveroProduct?) {
        setFieldValue(PRODUCT_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("FaveroProduct"))
    }

    /**
     * garmin_product: what product means when this message selects it.
     */
    public val garminProduct: GarminProduct?
        get() = (getFieldValue(PRODUCT_FIELD_NUM, 0, SubFieldSelector.Named("GarminProduct")) as? UShort)?.let { GarminProduct.fromValue(it) }

    public fun setGarminProduct(value: GarminProduct?) {
        setFieldValue(PRODUCT_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("GarminProduct"))
    }

    /** serial_number. */
    public var serialNumber: UInt?
        get() = getFieldValue(SERIAL_NUMBER_FIELD_NUM) as? UInt
        set(value) = setFieldValue(SERIAL_NUMBER_FIELD_NUM, value)

    /** time_created. Only set for files that are can be created/erased. */
    public var timeCreated: Instant?
        get() = (getFieldValue(TIME_CREATED_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIME_CREATED_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** number. Only set for files that are not created/erased. */
    public var number: UShort?
        get() = getFieldValue(NUMBER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(NUMBER_FIELD_NUM, value)

    /** product_name. Optional free form string to indicate the devices name or model */
    public var productName: String?
        get() = getFieldValue(PRODUCT_NAME_FIELD_NUM) as? String
        set(value) = setFieldValue(PRODUCT_NAME_FIELD_NUM, value)

    public companion object {
        public const val TYPE_FIELD_NUM: UByte = 0u
        public const val MANUFACTURER_FIELD_NUM: UByte = 1u
        public const val PRODUCT_FIELD_NUM: UByte = 2u
        public const val SERIAL_NUMBER_FIELD_NUM: UByte = 3u
        public const val TIME_CREATED_FIELD_NUM: UByte = 4u
        public const val NUMBER_FIELD_NUM: UByte = 5u
        public const val PRODUCT_NAME_FIELD_NUM: UByte = 8u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[0] = Field("Type", 0u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[1] = Field("Manufacturer", 1u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[2] = Field("Product", 2u, BaseType.UINT16, 1.0, 0.0, "", false)
                .addSubField(
                    SubField("FaveroProduct", BaseType.UINT16, 1.0, 0.0, "").also { subField ->
                        subField.addMap(1u, 263)
                    },
                )
                .addSubField(
                    SubField("GarminProduct", BaseType.UINT16, 1.0, 0.0, "").also { subField ->
                        subField.addMap(1u, 1)
                        subField.addMap(1u, 15)
                        subField.addMap(1u, 13)
                        subField.addMap(1u, 89)
                    },
                )
            fields[3] = Field("SerialNumber", 3u, BaseType.UINT32Z, 1.0, 0.0, "", false)
            fields[4] = Field("TimeCreated", 4u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[5] = Field("Number", 5u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[8] = Field("ProductName", 8u, BaseType.STRING, 1.0, 0.0, "", false)
            return fields
        }
    }
}
